import { clsx } from "clsx";
import type { ElementType } from "react";
import { createElement } from "react";
import { Box, type BoxElement, type BoxProps } from "../Box/Box";
import type { Breakpoint, ResponsiveSpace } from "../tokens";
import {
  breakpointOrder,
  columnGapBreakpointMap,
  columnGapMap,
  gapBreakpointMap,
  gapMap,
  resolveResponsive,
  rowGapBreakpointMap,
  rowGapMap,
} from "../tokens";

export type GridColumns = 1 | 2 | 3 | 4;
// Either a scalar (the ergonomic 1→2→3 ramp, unchanged) or an explicit per-breakpoint
// map on the curated scale — `base` (mobile-first) is required, each breakpoint optional.
export type ResponsiveColumns =
  | GridColumns
  | ({ base: GridColumns } & Partial<Record<Breakpoint, GridColumns>>);

type GridOwnProps = {
  columns?: ResponsiveColumns;
  gap?: ResponsiveSpace;
  rowGap?: ResponsiveSpace;
  columnGap?: ResponsiveSpace;
  align?: "start" | "center" | "end" | "stretch";
};

export type GridProps<T extends BoxElement = "div"> = BoxProps<T> & GridOwnProps;

// Scalar ramp (back-compat): columns={3} → grid-cols-1 md:grid-cols-2 lg:grid-cols-3.
const columnsMap = {
  1: "grid-cols-1",
  2: "grid-cols-1 md:grid-cols-2",
  3: "grid-cols-1 md:grid-cols-2 lg:grid-cols-3",
  4: "grid-cols-1 md:grid-cols-2 lg:grid-cols-4",
} as const;

// Base (unprefixed) column classes for the explicit map's `base` step.
const baseColumnsMap: Record<GridColumns, string> = {
  1: "grid-cols-1",
  2: "grid-cols-2",
  3: "grid-cols-3",
  4: "grid-cols-4",
} as const;

// Full literal per-breakpoint classes (Tailwind scans source for these exact strings).
// Only md/lg — the design system's sanctioned scale (banned-breakpoints, ADR-0081).
const breakpointColumnsMap: Record<Breakpoint, Record<GridColumns, string>> = {
  md: { 1: "md:grid-cols-1", 2: "md:grid-cols-2", 3: "md:grid-cols-3", 4: "md:grid-cols-4" },
  lg: { 1: "lg:grid-cols-1", 2: "lg:grid-cols-2", 3: "lg:grid-cols-3", 4: "lg:grid-cols-4" },
} as const;

function resolveColumns(columns: ResponsiveColumns): string {
  if (typeof columns === "number") return columnsMap[columns];
  const classes = [baseColumnsMap[columns.base]];
  for (const breakpoint of breakpointOrder) {
    const value = columns[breakpoint];
    if (value !== undefined) classes.push(breakpointColumnsMap[breakpoint][value]);
  }
  return classes.join(" ");
}

const alignMap = {
  start: "items-start",
  center: "items-center",
  end: "items-end",
  stretch: "items-stretch",
} as const;

export function Grid<T extends BoxElement = "div">({
  columns = 3,
  gap = 24,
  rowGap: rowGapProp,
  columnGap: columnGapProp,
  align = "stretch",
  className,
  ...boxProps
}: GridProps<T>) {
  const hasAxisGap = rowGapProp !== undefined || columnGapProp !== undefined;

  const classes = clsx(
    "grid",
    resolveColumns(columns),
    alignMap[align],
    !hasAxisGap && resolveResponsive(gap, gapMap, gapBreakpointMap),
    hasAxisGap && resolveResponsive(rowGapProp ?? gap, rowGapMap, rowGapBreakpointMap),
    hasAxisGap && resolveResponsive(columnGapProp ?? gap, columnGapMap, columnGapBreakpointMap),
    className,
  );

  return createElement(
    Box as unknown as ElementType,
    {
      "data-component": "Grid",
      ...boxProps,
      className: classes,
    } as unknown as BoxProps<T>,
  );
}

Grid.displayName = "Grid";
